// subscriptionUtils.js — shared helpers for the Subscription module
import React from 'react';
import { createRoot } from 'react-dom/client';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import InvoiceDocument from './InvoiceDocument';
import { getSubscriptionSettings, getMySubscription } from '../../services/SubscriptionService';

export const formatINR = (amount) => {
    const n = Number(amount) || 0;
    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(n);
};

export const formatDate = (unix) => {
    if (!unix) return '—';
    const d = new Date(Number(unix) * 1000);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

export const formatDateTime = (unix) => {
    if (!unix) return '—';
    const d = new Date(Number(unix) * 1000);
    return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

export const BILLING_CYCLES = [
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' },
    { value: 'half-yearly', label: 'Half-Yearly' },
    { value: 'yearly', label: 'Yearly' },
];

export const BILLING_CYCLE_DAYS = {
    monthly: 30,
    quarterly: 91,
    'half-yearly': 182,
    yearly: 365,
};

export const BILLING_CYCLE_MONTHS = {
    monthly: 1,
    quarterly: 3,
    'half-yearly': 6,
    yearly: 12,
};

export const formatBillingCycle = (cycle) => {
    const normalized = cycle === '3-monthly' ? 'quarterly' : cycle;
    const match = BILLING_CYCLES.find((c) => c.value === normalized);
    return match?.label || cycle || '—';
};

export const normalizeBillingCycle = (cycle) => (cycle === '3-monthly' ? 'quarterly' : cycle);

/** Master account, branch admin (supportAdminAccess), or both — not regular branch staff. */
export const canViewSubscriptionBilling = (userData) => {
    if (!userData || userData.role !== 2 || !userData.masterId) return false;
    return !!(userData.masterAccount || userData.supportAdminAccess);
};

const addMonthsToUnix = (unix, months) => {
    const d = new Date(Number(unix) * 1000);
    d.setMonth(d.getMonth() + months);
    return Math.floor(d.getTime() / 1000);
};

/**
 * Preview the new expiry after payment approval.
 * Early renewal (before current expiry): adds one cycle from existing expiry date.
 * Late renewal (after expiry): adds one cycle from now.
 */
export const computeRenewalEndDate = (subscription, extendFromExpiry = true, nowUnix = Math.floor(Date.now() / 1000)) => {
    const endDate = subscription?.endDate ? Number(subscription.endDate) : null;
    const rawCycle = subscription?.billingCycle || 'monthly';
    const billingCycle = normalizeBillingCycle(rawCycle);
    const isEarly = !!(extendFromExpiry && endDate && endDate > nowUnix);
    const base = isEarly ? endDate : nowUnix;
    const months = BILLING_CYCLE_MONTHS[billingCycle];
    if (months) return addMonthsToUnix(base, months);
    const days = BILLING_CYCLE_DAYS[billingCycle] || 30;
    return base + days * 86400;
};

export const statusMeta = (state) => {
    switch (state) {
        case 'active':
            return { label: 'Active', text: 'text-emerald-700', bg: 'bg-emerald-100', dot: 'bg-emerald-500' };
        case 'expiring':
            return { label: 'Expiring Soon', text: 'text-amber-700', bg: 'bg-amber-100', dot: 'bg-amber-500' };
        case 'grace':
            return { label: 'In Grace Period', text: 'text-orange-700', bg: 'bg-orange-100', dot: 'bg-orange-500' };
        case 'expired':
            return { label: 'Expired', text: 'text-rose-700', bg: 'bg-rose-100', dot: 'bg-rose-500' };
        case 'none':
        default:
            return { label: 'Not Configured', text: 'text-gray-600', bg: 'bg-gray-100', dot: 'bg-gray-400' };
    }
};

const isNonEmpty = (v) => v != null && String(v).trim() !== '';

const pickNonEmpty = (...vals) => {
    for (const v of vals) {
        if (isNonEmpty(v)) return v;
    }
    return '';
};

const mergeCompanyFromSettings = (invoiceCompany, settings) => {
    const ic = invoiceCompany || {};
    const sc = settings?.company || {};
    return {
        name: pickNonEmpty(ic.name, sc.name),
        legalName: pickNonEmpty(ic.legalName, sc.legalName),
        addressLine1: pickNonEmpty(ic.addressLine1, sc.addressLine1),
        addressLine2: pickNonEmpty(ic.addressLine2, sc.addressLine2),
        city: pickNonEmpty(ic.city, sc.city),
        state: pickNonEmpty(ic.state, sc.state),
        pincode: pickNonEmpty(ic.pincode, sc.pincode),
        gstin: pickNonEmpty(ic.gstin, sc.gstin),
        email: pickNonEmpty(ic.email, sc.email),
        phone: pickNonEmpty(ic.phone, sc.phone),
        website: pickNonEmpty(ic.website, sc.website),
        logoUrl: pickNonEmpty(ic.logoUrl, sc.logoUrl),
        upiId: pickNonEmpty(ic.upiId, settings?.upiId),
        upiName: pickNonEmpty(ic.upiName, settings?.upiName),
        bankDetails: { ...(settings?.bankDetails || {}), ...(ic.bankDetails || {}) },
    };
};

// Load remote image as base64 data URL so html2canvas can embed it in the PDF.
const loadImageAsDataUrl = async (url) => {
    if (!url) return null;
    if (url.startsWith('data:')) return url;

    // Prefer fetch → blob (works when S3 allows GET CORS)
    try {
        const res = await fetch(url, { method: 'GET', mode: 'cors', cache: 'no-cache' });
        if (res.ok) {
            const blob = await res.blob();
            const dataUrl = await new Promise((resolve) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.onerror = () => resolve(null);
                reader.readAsDataURL(blob);
            });
            if (dataUrl) return dataUrl;
        }
    } catch (e) {
        // fall through to canvas approach
    }

    return new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            try {
                const canvas = document.createElement('canvas');
                canvas.width = img.naturalWidth || 200;
                canvas.height = img.naturalHeight || 200;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0);
                resolve(canvas.toDataURL('image/png'));
            } catch (e) {
                resolve(url);
            }
        };
        img.onerror = () => resolve(url);
        img.src = url;
    });
};

const waitForImages = (rootEl, timeoutMs = 5000) =>
    new Promise((resolve) => {
        const imgs = rootEl ? [...rootEl.querySelectorAll('img')] : [];
        if (!imgs.length) {
            resolve();
            return;
        }
        let pending = imgs.length;
        const timer = setTimeout(resolve, timeoutMs);
        const tick = () => {
            pending -= 1;
            if (pending <= 0) {
                clearTimeout(timer);
                resolve();
            }
        };
        imgs.forEach((img) => {
            if (img.complete) {
                tick();
            } else {
                img.addEventListener('load', tick, { once: true });
                img.addEventListener('error', tick, { once: true });
            }
        });
        if (pending <= 0) {
            clearTimeout(timer);
            resolve();
        }
    });

/**
 * Render the styled HTML invoice off-screen, capture it with html2canvas and
 * save it as a crisp multi-page A4 PDF. Renders the rupee symbol and uploaded
 * company/client logos exactly as the browser draws them.
 */
export const downloadInvoicePdf = async (invoice) => {
    if (!invoice) return;

    let enriched = {
        ...invoice,
        company: mergeCompanyFromSettings(invoice.company, {}),
        client: { ...(invoice.client || {}) },
    };

    // Merge latest billing settings so FROM address & logos appear even on older invoice snapshots
    try {
        const user = JSON.parse(localStorage.getItem('userData') || '{}');
        if (user.role === 1) {
            const settingsRes = await getSubscriptionSettings();
            if (settingsRes?.success && settingsRes.data) {
                enriched.company = mergeCompanyFromSettings(invoice.company, settingsRes.data);
            }
        } else if (user.role === 2) {
            const mySub = await getMySubscription();
            if (mySub?.success && mySub.data?.settings) {
                enriched.company = mergeCompanyFromSettings(invoice.company, mySub.data.settings);
            }
            const subLogo = mySub?.data?.subscription?.clientLogoUrl;
            if (subLogo) {
                enriched.client.logoUrl = pickNonEmpty(enriched.client.logoUrl, subLogo);
            }
        }
    } catch (e) {
        // non-blocking
    }

    const companyLogoUrl = enriched.company?.logoUrl || '';
    const clientLogoUrl = enriched.client?.logoUrl || '';

    const [companyLogo, clientLogo] = await Promise.all([
        loadImageAsDataUrl(companyLogoUrl),
        loadImageAsDataUrl(clientLogoUrl),
    ]);

    const container = document.createElement('div');
    Object.assign(container.style, {
        position: 'fixed',
        left: '-10000px',
        top: '0',
        width: '794px',
        background: '#ffffff',
        zIndex: '-1',
    });
    document.body.appendChild(container);

    const root = createRoot(container);
    root.render(
        React.createElement(InvoiceDocument, { invoice: enriched, companyLogo, clientLogo })
    );

    await new Promise((resolve) => setTimeout(resolve, 100));
    const target = container.firstElementChild || container;
    await waitForImages(target, 5000);
    await new Promise((resolve) => setTimeout(resolve, 200));

    try {
        const canvas = await html2canvas(target, {
            scale: 2,
            useCORS: true,
            allowTaint: true,
            backgroundColor: '#ffffff',
            logging: false,
            imageTimeout: 8000,
            width: 794,
        });

        const pdf = new jsPDF('p', 'mm', 'a4');
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const imgWidth = pageWidth;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        const imgData = canvas.toDataURL('image/png');

        let heightLeft = imgHeight;
        let position = 0;
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
        while (heightLeft > 0) {
            position -= pageHeight;
            pdf.addPage();
            pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
            heightLeft -= pageHeight;
        }

        pdf.save(`Invoice_${(invoice.invoiceNumber || 'subscription').replace(/[\\/]/g, '-')}.pdf`);
    } finally {
        try { root.unmount(); } catch (e) { /* noop */ }
        container.remove();
    }
};
